import React, { FC, useEffect, useRef, useState } from 'react'

import { alertError, alertSuccess } from '../../../../api'
import { CarePlan } from 'models/ServiceUsersTypes'
import {
  AuditStatus,
  CarePlanAuditPayload,
  CarePlanAuditVersion,
  getCarePlanAudit,
  getCarePlanAuditStatusDetails,
} from '../../api/carePlanAuditApi'
import {
  ALREADY_VIEWED_AUDIT_REASON,
  auditBlockedReasonForUser,
  formatCarePlanAuditQuotaReason,
  PENDING_CARE_PLAN_REASON,
} from '../../helpers/carePlanAuditGate'
import { useBotUsageContext } from 'contexts/BotUsageContext'
import { USAGE_METRIC } from 'constants/usageLimits'
import { AuditReport } from './auditReportMock'
import { mapCarePlanAuditToReport } from './mapCarePlanAuditToReport'
import * as S from './AuditResult.styles'
import Tooltip from 'uiKit/Tooltip'

interface Props {
  open: boolean
  onClose: () => void
  patientName: string
  filename: string
  /** All care-plan filenames for this service user (sidebar list). */
  carePlans?: string[]
  /**
   * Live care-plan records (with `status`) for the open service user — used to
   * disable Run when any file is still training, even if the parent
   * `blockedReason` expression missed them.
   */
  carePlanFiles?: CarePlan[] | null
  /** The organisation and service user the history belongs to. */
  botId?: string | number
  patientId?: number
  /**
   * Live status of the generate call from the table.
   * Undefined = idle. PROCESSING while POST is in flight; READY after success.
   */
  status?: AuditStatus
  /**
   * Latest report version the table already knows about. Lets the run labels
   * say "Run new audit" on first paint instead of waiting for the history GET.
   */
  knownVersion?: number
  /**
   * Immediate report from a successful POST — paints before any follow-up GET
   * so READY never flashes an empty main column.
   */
  seedPayload?: CarePlanAuditPayload | null
  /**
   * Charges the allowance and starts an audit. The only thing that starts one.
   * Resolves true when a new report was produced.
   */
  onRun: () => void | Promise<boolean>
  /** Downloads the PDF for the version currently on screen. */
  onDownload?: (version?: number) => void
  /**
   * Optional precomputed block reason (legacy). Prefer the usage context inside
   * this component so Run New Audit matches the table.
   */
  blockedReason?: string
  /**
   * Notifies the table when server-side report generation starts or stops
   * (`GET …/care-plan-audit/status` → `report_generating`).
   */
  onReportGeneratingChange?: (generating: boolean) => void
}

/**
 * The CQC fundamental standards the audit reports against.
 *
 * Listed on the resting state so the reader knows what they are about to spend
 * an audit on. Presentational copy for this screen — the finished report builds
 * its own section titles.
 */
const CHECK_AREAS = [
  'Person-Centred Care',
  'Dignity and Respect',
  'Need for Consent',
  'Safe Care and Treatment',
  'Safeguarding from Abuse',
  'Nutrition and Hydration',
  'Premises and Equipment',
  'Good Governance',
]

/** Inline Lucide-style glyphs (the repo ships no lucide package). */
const Icon: FC<{ paths: string[]; size?: number; sw?: number }> = ({ paths, size = 18, sw = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    {paths.map((d, i) => (
      <path key={i} d={d} stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
    ))}
  </svg>
)

const ICON = {
  back: ['M19 12H5', 'M12 19l-7-7 7-7'],
  file: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6'],
  sparkles: [
    'M12 3l1.7 4.3L18 9l-4.3 1.7L12 15l-1.7-4.3L6 9l4.3-1.7z',
    'M19 14l.7 1.8L21.5 16.5l-1.8.7L19 19l-.7-1.8L16.5 16.5l1.8-.7z',
  ],
  check: ['M22 11.1V12a10 10 0 1 1-5.9-9.1', 'M22 4L12 14.01l-3-3'],
  bulb: ['M9 18h6', 'M10 22h4', 'M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.3h6c0-1 .4-1.8 1-2.3A7 7 0 0 0 12 2z'],
  alert: [
    'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z',
    'M12 9v4',
    'M12 17h.01',
  ],
  download: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3'],
  copy: [
    'M20 9h-9a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2Z',
    'M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1',
  ],
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const pad = (n: number) => String(n).padStart(2, '0')

const formatWhen = (iso?: string): string => {
  if (!iso) return 'Recently'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'Recently'
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/**
 * Renders the report as plain text for the clipboard — same content and order
 * as the page, so a paste into email/case notes matches what was on screen.
 */
const buildReportText = (report: AuditReport): string => {
  const lines: string[] = [
    `Care plan review — ${report.patientName}`,
    `reviewed ${report.reviewedAt} by ${report.reviewer}`,
    '',
  ]

  if (report.summary) {
    lines.push(report.summary, '')
  }

  report.sections.forEach(section => {
    lines.push(section.title)
    if (section.strengths.length) {
      lines.push('Strengths:')
      section.strengths.forEach(item => lines.push(`- ${item}`))
    }
    if (section.recommendations.length) {
      lines.push('Recommendations for improvement:')
      section.recommendations.forEach(item => lines.push(`- ${item}`))
    }
    lines.push('')
  })

  if (report.inconsistencies.length) {
    lines.push('Inconsistencies:')
    report.inconsistencies.forEach(item => lines.push(`- ${item}`))
  }

  return lines.join('\n').trim()
}

export const AuditResult: FC<Props> = ({
  open,
  onClose,
  patientName,
  filename,
  carePlans,
  carePlanFiles,
  botId,
  patientId,
  status,
  knownVersion,
  seedPayload,
  onRun,
  onDownload,
  blockedReason: _blockedReason,
  onReportGeneratingChange,
}) => {
  const [payload, setPayload] = useState<CarePlanAuditPayload | null>(null)
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
  const [loadingVersion, setLoadingVersion] = useState(false)
  const [reportGenerating, setReportGenerating] = useState(false)
  // `already_viewed` from the status endpoint — blocks a repeat run on the same care plan(s).
  const [alreadyViewed, setAlreadyViewed] = useState(false)
  // Bumped after a run settles so the status endpoint is asked again.
  const [statusCheck, setStatusCheck] = useState(0)
  // Service user whose run was started from this screen and has not settled.
  const [runningFor, setRunningFor] = useState<number | null>(null)

  // While true, a successful generate lands on the newest report. Open / reopen
  // leaves selection empty so the ready checklist shows until the reader picks
  // a version (or runs an audit). Picking a version from the list opts out.
  const followLatestRef = useRef(false)
  const versionRequestRef = useRef<AbortController | null>(null)
  const selectedVersionRef = useRef<number | null>(null)
  const payloadRef = useRef<CarePlanAuditPayload | null>(null)
  const reportGeneratingRef = useRef(false)
  selectedVersionRef.current = selectedVersion

  // Prefer POST seed on the same render as READY — do not wait for useEffect
  // or the main column flashes empty for a frame.
  const displayPayload = payload ?? (seedPayload?.current ? seedPayload : null)
  payloadRef.current = displayPayload

  // Live usage from the page provider — same source as the table tip.
  const usage = useBotUsageContext()
  const localQuota = formatCarePlanAuditQuotaReason(usage?.metric(USAGE_METRIC.CARE_PLAN_AUDITS))
  const runBlockedReason = auditBlockedReasonForUser(
    carePlanFiles,
    localQuota,
    { packageEnabled: usage ? usage.isFeatureEnabled(USAGE_METRIC.CARE_PLAN_AUDITS) : true },
  )

  const processing = runningFor != null && runningFor === patientId
  // Server-side work this screen did not start (post-upload processing, a run
  // from elsewhere): keep the screen at rest, block Run and say why.
  const serverBusy = status === 'PENDING' || status === 'PROCESSING' || reportGenerating
  const runTip =
    runBlockedReason ||
    (serverBusy && !processing ? PENDING_CARE_PLAN_REASON : undefined) ||
    (alreadyViewed && !processing ? ALREADY_VIEWED_AUDIT_REASON : undefined)

  // Open with no version selected — ready checklist until manual pick or a run.
  useEffect(() => {
    followLatestRef.current = false
    setSelectedVersion(null)
  }, [open, botId, patientId])

  // Sync seed into state for the Reviews list — do not auto-select latest.
  useEffect(() => {
    if (!open || !seedPayload?.current) return
    setPayload(seedPayload)
    if (followLatestRef.current) {
      setSelectedVersion(seedPayload.current.version)
    }
  }, [open, seedPayload])

  // Server-side generate status — poll while report_generating is true.
  useEffect(() => {
    if (!open || !botId || !patientId) {
      setReportGenerating(false)
      setAlreadyViewed(false)
      reportGeneratingRef.current = false
      return undefined
    }

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    const controller = new AbortController()
    const POLL_MS = 3000

    const applyGenerating = (generating: boolean) => {
      if (cancelled) return
      const was = reportGeneratingRef.current
      reportGeneratingRef.current = generating
      setReportGenerating(generating)
      if (was !== generating) {
        onReportGeneratingChange?.(generating)
      }
      if (was && !generating) {
        // Generation finished elsewhere — refresh history/report.
        getCarePlanAudit(botId, patientId, undefined, controller.signal)
          .then(data => {
            if (cancelled || controller.signal.aborted || !data) return
            setPayload(data)
            if (data.current?.version != null) {
              setSelectedVersion(prev => {
                if (followLatestRef.current) return data.current.version
                if (prev != null && data.versions.some(v => v.version === prev)) return prev
                return null
              })
            }
          })
          .catch(error => {
            if (error?.name === 'AbortError') return
          })
      }
    }

    const tick = () => {
      getCarePlanAuditStatusDetails(botId, patientId, controller.signal)
        .then(({ reportGenerating: generating, alreadyViewed: viewed }) => {
          if (cancelled) return
          applyGenerating(generating)
          setAlreadyViewed(viewed)
          if (generating) {
            timer = setTimeout(tick, POLL_MS)
          }
        })
        .catch(error => {
          if (error?.name === 'AbortError' || cancelled) return
          applyGenerating(false)
          setAlreadyViewed(false)
        })
    }

    tick()

    return () => {
      cancelled = true
      controller.abort()
      if (timer) clearTimeout(timer)
    }
  }, [open, botId, patientId, onReportGeneratingChange, statusCheck])

  useEffect(() => {
    if (!open || !botId || !patientId) {
      setPayload(null)
      setSelectedVersion(null)
      return undefined
    }

    // While a generate is in flight, skip GET only when we already have a report
    // to keep under the veil (avoids racing a just-posted seed with a stale list).
    // If we opened mid-run with no payload/seed, still hydrate the prior report.
    const generating = status === 'PENDING' || status === 'PROCESSING' || reportGenerating
    if (generating && (payloadRef.current?.current || seedPayload?.current)) {
      return undefined
    }

    const controller = new AbortController()
    getCarePlanAudit(botId, patientId, undefined, controller.signal)
      .then(data => {
        if (controller.signal.aborted) return
        // Soft failure: keep a seeded/previous payload if GET returns nothing.
        if (!data) return
        setPayload(data)
        if (data.current?.version != null) {
          setSelectedVersion(prev => {
            if (followLatestRef.current) return data.current.version
            if (prev != null && data.versions.some(v => v.version === prev)) return prev
            return null
          })
        }
      })
      .catch(error => {
        if (error?.name === 'AbortError') return
        // Soft failure — do not wipe a report we already have on screen.
      })

    // `status` dependency: after READY, re-fetch so history stays in sync.
    return () => controller.abort()
  }, [open, botId, patientId, status, reportGenerating, seedPayload])

  // Abort in-flight version fetches when the overlay closes so the next open
  // does not inherit a stuck loading veil.
  useEffect(() => {
    if (open) return undefined
    versionRequestRef.current?.abort()
    versionRequestRef.current = null
    setLoadingVersion(false)
    return undefined
  }, [open])

  useEffect(
    () => () => {
      versionRequestRef.current?.abort()
    },
    [],
  )

  const handleRun = () => {
    if (runTip || processing) return
    followLatestRef.current = true
    const runPatient = patientId ?? null
    setRunningFor(runPatient)
    Promise.resolve<boolean | void>(onRun())
      .then(produced => {
        // A rejected / failed run must not jump to an older report.
        if (produced === false) followLatestRef.current = false
      })
      .finally(() => {
        setRunningFor(prev => (prev === runPatient ? null : prev))
        setStatusCheck(n => n + 1)
      })
  }

  const handleCopy = (reportText: string) => {
    navigator.clipboard
      .writeText(reportText)
      .then(() => alertSuccess('Report copied to clipboard.', 1500))
      .catch(() => alertError('Could not copy the report.'))
  }

  const selectVersion = (version: number) => {
    if (!botId || !patientId || version === selectedVersionRef.current) return
    followLatestRef.current = false
    const previousSelection = selectedVersionRef.current
    setSelectedVersion(version)
    setLoadingVersion(true)

    versionRequestRef.current?.abort()
    const controller = new AbortController()
    versionRequestRef.current = controller

    getCarePlanAudit(botId, patientId, version, controller.signal)
      .then(data => {
        if (controller.signal.aborted) return
        if (data) {
          setPayload(data)
          return
        }
        // Failed fetch — restore prior selection (may be null). Never jump to latest.
        setSelectedVersion(previousSelection)
      })
      .catch(error => {
        if (error?.name === 'AbortError') return
        setSelectedVersion(previousSelection)
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingVersion(false)
      })
  }

  if (!open) return null

  const failed = status === 'FAILED'
  const versions: CarePlanAuditVersion[] = displayPayload?.versions || []
  const hasVersions = versions.length > 0
  // Ready checklist until the reader picks a version (or a run selects latest).
  // Processing keeps the run banner instead of the ready card.
  const showReadyState = !processing && selectedVersion == null

  // Explicit selection only — no fallback to "current"/latest on open.
  const activeVersion = selectedVersion

  // Show Reviews whenever history exists so a single prior audit is still reachable.
  const showReviewsList = hasVersions

  const report = displayPayload?.current
    ? mapCarePlanAuditToReport(displayPayload, filename, patientName)
    : {
      patientName: patientName || 'Service user',
      filename: filename || 'care-plan.pdf',
      reviewedAt: '—',
      reviewer: 'QCS AI Auditor',
      cqcAlignment: 0,
      summary: '',
      sections: [] as { title: string; strengths: string[]; recommendations: string[] }[],
      inconsistencies: [] as string[],
    }

  const hasAlignmentField =
    displayPayload?.current != null &&
    (displayPayload.current.cqcAlignment != null || displayPayload.current.alignment != null)
  const showScore = Boolean(hasAlignmentField)
  // History from the GET, or the version the table already knew, so the label
  // does not flip from "Run care plan audit" to "Run new audit" after load.
  const hasHistory = hasVersions || knownVersion != null
  const runLabel = processing ? 'Running…' : hasHistory ? 'Run new audit' : 'Run care plan audit'
  const readyCtaLabel = hasHistory ? 'Run new audit' : 'Run care plan audit'
  const canDownload = Boolean(onDownload) && activeVersion != null && !processing
  // Failure replaces the processing slot — do not stack both banners or keep
  // the prior report blurred under GlassVeil after a failed re-run.
  const showProcessing = (processing || loadingVersion) && !failed
  const plans = carePlans && carePlans.length ? carePlans : [report.filename]

  return (
    <S.Overlay role="dialog" aria-modal="true" aria-label="Care plan audit">
      <S.TopBar>
        <S.BackButton onClick={onClose}>
          <Icon paths={ICON.back} size={16} />
          Back
        </S.BackButton>
        <S.TopMeta>
          <S.TopEyebrow>AI care plan audit</S.TopEyebrow>
          <S.TopTitle>{report.patientName}</S.TopTitle>
        </S.TopMeta>
      </S.TopBar>

      <S.Layout>
        <S.Sidebar>
          <S.SideCard>
            <S.SideLabel>
              Care plans
              {plans.length > 1 && <S.SideCount>{plans.length}</S.SideCount>}
            </S.SideLabel>
            <S.FileList>
              {plans.map((name, i) => (
                <S.FileChip key={`${name}-${i}`}>
                  <Icon paths={ICON.file} size={16} />
                  <span title={name}>{name}</span>
                </S.FileChip>
              ))}
            </S.FileList>
            {!showReadyState && (
              runTip ? (
                <>
                  <S.RunButtonTipAnchor data-for="audit-run-blocked" data-tip>
                    <S.RunButton disabled onClick={handleRun}>
                      <Icon paths={ICON.sparkles} size={16} sw={1.6} />
                      {runLabel}
                    </S.RunButton>
                  </S.RunButtonTipAnchor>
                  <Tooltip id="audit-run-blocked">{runTip}</Tooltip>
                </>
              ) : (
                <S.RunButton disabled={processing} onClick={handleRun}>
                  <Icon paths={ICON.sparkles} size={16} sw={1.6} />
                  {runLabel}
                </S.RunButton>
              )
            )}
            {canDownload && (
              <S.DownloadButton onClick={() => onDownload && onDownload(activeVersion ?? undefined)}>
                <Icon paths={ICON.download} size={16} />
                Download PDF
              </S.DownloadButton>
            )}
          </S.SideCard>

          {showReviewsList && (
            <S.SideCard>
              <S.SideLabel>Reviews</S.SideLabel>
              <S.VersionList>
                {versions.map((version, i) => (
                  <S.VersionItem
                    key={version.version}
                    selected={version.version === activeVersion}
                    onClick={() => selectVersion(version.version)}>
                    <S.VersionName>
                      Version {version.version}
                      {version.latest || i === 0 ? ' · latest' : ''}
                    </S.VersionName>
                    <S.VersionMeta>{formatWhen(version.createdAt)}</S.VersionMeta>
                  </S.VersionItem>
                ))}
              </S.VersionList>
            </S.SideCard>
          )}
        </S.Sidebar>

        <S.Main>
          <S.ProcInline visible={showProcessing}>
            <S.ProcOrb>
              <Icon paths={ICON.sparkles} size={20} sw={1.6} />
            </S.ProcOrb>
            <S.ProcTextWrap>
              <S.ProcTitle>
                Reviewing care plan<i>.</i>
                <i>.</i>
                <i>.</i>
              </S.ProcTitle>
              <S.ProcSub>Checking care plan content against CQC standards</S.ProcSub>
            </S.ProcTextWrap>
          </S.ProcInline>

          <S.ErrorInline visible={failed && !showReadyState}>
            <S.ErrorOrb>
              <Icon paths={ICON.alert} size={20} sw={1.7} />
            </S.ErrorOrb>
            <S.ProcTextWrap>
              <S.ErrorTitle>We could not finish this audit</S.ErrorTitle>
              <S.ProcSub>The review did not complete. Run it again, or contact support if it keeps failing.</S.ProcSub>
            </S.ProcTextWrap>
          </S.ErrorInline>

          {showReadyState && (
            <S.ReadyArea>
              {/* Prototype order: checklist first, then the ready CTA card. */}
              <S.ChecksCard>
                <S.ChecksTitle>What the audit checks</S.ChecksTitle>
                <S.ChecksSub>The eight CQC fundamental standards, reviewed one by one.</S.ChecksSub>
                <S.ChecksGrid>
                  {CHECK_AREAS.map(area => (
                    <S.CheckItem key={area}>
                      <S.CheckDot>
                        <Icon paths={ICON.check} size={14} />
                      </S.CheckDot>
                      {area}
                    </S.CheckItem>
                  ))}
                </S.ChecksGrid>
              </S.ChecksCard>

              <S.ReadyCard>
                <S.ReadyOrb>
                  <Icon paths={ICON.sparkles} size={26} sw={1.5} />
                </S.ReadyOrb>
                <S.ReadyTitle>Ready to audit {report.patientName} care plan</S.ReadyTitle>
                <S.ReadyText>
                  Review this care plan against the fundamental standards and identify strengths, recommendations and
                  inconsistencies.
                </S.ReadyText>
                {runTip ? (
                  <>
                    <S.ReadyCtaTipAnchor data-for="audit-ready-blocked" data-tip>
                      <S.ReadyCta onClick={handleRun} disabled>
                        <Icon paths={ICON.sparkles} size={17} sw={1.6} />
                        {readyCtaLabel}
                      </S.ReadyCta>
                    </S.ReadyCtaTipAnchor>
                    <Tooltip id="audit-ready-blocked">{runTip}</Tooltip>
                  </>
                ) : (
                  <S.ReadyCta onClick={handleRun}>
                    <Icon paths={ICON.sparkles} size={17} sw={1.6} />
                    {readyCtaLabel}
                  </S.ReadyCta>
                )}
                {runTip && <S.ReadyBlocked>{runTip}</S.ReadyBlocked>}
              </S.ReadyCard>
            </S.ReadyArea>
          )}

          {!showReadyState && displayPayload?.current && (
            <S.SectionsArea>
              <S.Sections processing={showProcessing}>
                <S.ReportDoc>
                  <S.DocHeader>
                    <S.CopyButton type="button" onClick={() => handleCopy(buildReportText(report))}>
                      <Icon paths={ICON.copy} size={14} sw={1.8} />
                      Copy
                    </S.CopyButton>
                    <S.DocEyebrow>
                      <Icon paths={ICON.sparkles} size={13} sw={1.6} />
                      QCS AI care plan audit
                    </S.DocEyebrow>
                    <S.DocTitle>Care plan review — {report.patientName}</S.DocTitle>
                    <S.DocMeta>
                      reviewed {report.reviewedAt} by {report.reviewer}
                    </S.DocMeta>
                    {showScore && (
                      <S.DocScoreRow>
                        <S.DocScoreLabel>CQC alignment</S.DocScoreLabel>
                        <S.ScoreTrack>
                          <S.ScoreFill pct={report.cqcAlignment} />
                        </S.ScoreTrack>
                        <S.ScoreValue>{report.cqcAlignment}%</S.ScoreValue>
                      </S.DocScoreRow>
                    )}
                  </S.DocHeader>

                  <S.DocBody>
                    {report.sections.map(section => (
                      <S.DocSection key={section.title}>
                        <S.DocSectionTitle>{section.title}</S.DocSectionTitle>

                        {section.strengths.length > 0 && (
                          <S.Block>
                            <S.BlockHead tone="good">
                              <Icon paths={ICON.check} size={15} />
                              Strengths
                            </S.BlockHead>
                            <S.List>
                              {section.strengths.map((text, j) => (
                                <S.Item key={j}>{text}</S.Item>
                              ))}
                            </S.List>
                          </S.Block>
                        )}

                        {section.recommendations.length > 0 && (
                          <S.Block>
                            <S.BlockHead tone="improve">
                              <Icon paths={ICON.bulb} size={15} />
                              Recommendations for improvement
                            </S.BlockHead>
                            <S.List>
                              {section.recommendations.map((text, j) => (
                                <S.Item key={j}>{text}</S.Item>
                              ))}
                            </S.List>
                          </S.Block>
                        )}
                      </S.DocSection>
                    ))}

                    {report.inconsistencies.length > 0 && (
                      <S.DocSection tone="alert">
                        <S.DocSectionTitle tone="alert">
                          <Icon paths={ICON.alert} size={16} />
                          Inconsistencies
                        </S.DocSectionTitle>
                        <S.List>
                          {report.inconsistencies.map((text, i) => (
                            <S.Item key={i} tone="alert">
                              {text}
                            </S.Item>
                          ))}
                        </S.List>
                      </S.DocSection>
                    )}

                    {report.summary ? <S.DocFootnote>{report.summary}</S.DocFootnote> : null}
                    <S.DocSignoff>
                      Generated by {report.reviewer} · {report.reviewedAt}
                    </S.DocSignoff>
                  </S.DocBody>
                </S.ReportDoc>
              </S.Sections>

              <S.GlassVeil visible={showProcessing} />
            </S.SectionsArea>
          )}
        </S.Main>
      </S.Layout>
    </S.Overlay>
  )
}

export default AuditResult
